<div class="container">
  <app-loading [showLoading]="isLoading"></app-loading>
  <h1 class="header">
    <span class="title">Amazon Connect API キー管理</span
    ><button
      type="button"
      class="close-button"
      aria-label="閉じる"
      [disabled]="isLoading"
      (click)="close()"
    >
      @if (isCloseConfirmationVisible) {
        閉じる
      } @else {
        <mat-icon>close</mat-icon>
      }
    </button>
  </h1>
  @if (isCloseConfirmationVisible) {
    <p class="warning" role="alert">
      表示中の API
      キーは閉じると二度と確認できません。保存済みであれば「閉じる」を押してください。
    </p>
  }
  <div class="main">
    @if (data.intakeActive === false) {
      <p class="warning" role="status">
        取り込み受付は停止中です。発行したキーは受付を ON
        にするまで入稿に使えません。
      </p>
    }
    @if (issuedKey !== null) {
      <div
        #issuedKeyResult
        class="issued-key"
        role="status"
        tabindex="-1"
        aria-label="発行した API キー"
      >
        <label for="issued-key">発行した API キー</label>
        <p class="warning">
          このキーは再表示できません。今すぐ安全な場所に保存してください。
        </p>
        <div class="actions">
          <input
            id="issued-key"
            class="input"
            readonly
            autocomplete="off"
            [value]="issuedKey"
          /><button
            type="button"
            rectangle
            variant="secondary"
            (click)="copyKey()"
          >
            コピー
          </button>
        </div>
      </div>
    }

    @if (revokingKey; as key) {
      <div class="revoke-form">
        <label for="revoke-reason"
          >キー {{ key.keyId }} の失効理由（必須）</label
        >
        <input
          #revokeReasonInput
          id="revoke-reason"
          class="input"
          [formControl]="revokeReason"
          maxlength="200"
        />
        <p>このキーは再び有効にできません。</p>
        <div class="actions">
          <button
            type="button"
            rectangle
            variant="secondary"
            [disabled]="isLoading"
            (click)="cancelRevoke()"
          >
            キャンセル</button
          ><button
            type="button"
            rectangle
            [disabled]="
              isLoading ||
              revokeReason.invalid ||
              revokeReason.value.trim() === ''
            "
            (click)="revokeKey()"
          >
            失効する
          </button>
        </div>
      </div>
    }

    <div class="table-container">
      <table mat-table [dataSource]="keys">
        <ng-container matColumnDef="label"
          ><th mat-header-cell *matHeaderCellDef>ラベル</th>
          <td mat-cell *matCellDef="let key">
            {{ key.label === null ? '—' : key.label }}
          </td></ng-container
        >
        <ng-container matColumnDef="keyId"
          ><th mat-header-cell *matHeaderCellDef>キー ID</th>
          <td mat-cell *matCellDef="let key">{{ key.keyId }}</td></ng-container
        >
        <ng-container matColumnDef="last4"
          ><th mat-header-cell *matHeaderCellDef>末尾 4 桁</th>
          <td mat-cell *matCellDef="let key">
            ****{{ key.last4 }}
          </td></ng-container
        >
        <ng-container matColumnDef="status"
          ><th mat-header-cell *matHeaderCellDef>状態</th>
          <td mat-cell *matCellDef="let index = index">
            @let key = keys[index];
            <span
              class="status"
              [class.active]="key.status === activeStatus"
              [class.expired]="key.status === expiredStatus"
              >{{ statusLabels[key.status] }}</span
            >
          </td></ng-container
        >
        <ng-container matColumnDef="created"
          ><th mat-header-cell *matHeaderCellDef>発行日時・発行者</th>
          <td mat-cell *matCellDef="let key">
            {{ key.createdAt | date: 'yyyy/MM/dd HH:mm' }}<br />{{
              key.createdByName ?? key.createdBy
            }}
          </td></ng-container
        >
        <ng-container matColumnDef="lastUsedAt"
          ><th mat-header-cell *matHeaderCellDef>最終使用日時</th>
          <td mat-cell *matCellDef="let key">
            {{
              key.lastUsedAt === null
                ? '未使用'
                : (key.lastUsedAt | date: 'yyyy/MM/dd HH:mm')
            }}
          </td></ng-container
        >
        <ng-container matColumnDef="expiresAt"
          ><th mat-header-cell *matHeaderCellDef>有効期限</th>
          <td mat-cell *matCellDef="let key">
            {{ key.expiresAt | date: 'yyyy/MM/dd HH:mm' }}
          </td></ng-container
        >
        <ng-container matColumnDef="actions"
          ><th mat-header-cell *matHeaderCellDef>操作</th>
          <td mat-cell *matCellDef="let key">
            @if (key.status === activeStatus) {
              <button
                type="button"
                rectangle
                variant="secondary"
                [disabled]="isLoading"
                (click)="startRevoke(key)"
              >
                失効
              </button>
            }
          </td></ng-container
        >
        <tr mat-header-row *matHeaderRowDef="columns"></tr>
        <tr mat-row *matRowDef="let row; columns: columns"></tr>
        <tr class="mat-row" *matNoDataRow>
          <td class="empty" [attr.colspan]="columns.length">
            発行済みの API キーはありません。
          </td>
        </tr>
      </table>
    </div>
    <form class="issue-form" (submit)="$event.preventDefault(); issueKey()">
      <h2>API キーの発行</h2>
      <label for="api-key-label">ラベル（任意、100 文字まで）</label>
      <input
        id="api-key-label"
        class="input"
        [formControl]="label"
        maxlength="100"
      />
      <label for="api-key-expiry">有効期限（任意）</label>
      <app-date-input
        [id]="'api-key-expiry'"
        [inputDate]="expiresAt"
      ></app-date-input>
      @if (expiresAt.value === null) {
        <p>未指定の場合は発行から 1 年（最短でも 1 日以上先）</p>
      }
      @if (activeKeyCount >= maxActiveInboundApiKeys) {
        <p class="warning">
          同時に有効にできる API キーは {{ maxActiveInboundApiKeys }}
          本までです。不要なキーを失効してください。
        </p>
      }
      <button
        type="submit"
        rectangle
        data-testid="issue-key"
        [disabled]="isIssueDisabled"
      >
        API キーを発行
      </button>
    </form>
    @if (errorMessage) {
      <div class="error-message" role="alert">{{ errorMessage }}</div>
    }
    @if (!hasLoaded && !isLoading) {
      <button type="button" rectangle variant="secondary" (click)="loadKeys()">
        一覧を再取得
      </button>
    }
  </div>
</div>
